class S3FileBrowserController {
  constructor($scope, $http, $rootScope, $uibModal, toastr, $element) {
    "ngInject";
    
    this.$scope = $scope;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.$uibModal = $uibModal;
    this.toastr = toastr;
    this.$element = $element;
    
    // Current folder path (empty string for root) - display path (human-readable)
    this.currentPath = '';
    this.pathHistory = []; // Stack for navigation history (display paths)
    // Actual path with IDs for API calls
    this.actualCurrentPath = '';
    this.actualPathHistory = []; // Stack for navigation history (actual paths)
    this.breadcrumbs = [{ name: 'Home', restPath: '', displayPath: '' }]; // Cached breadcrumbs
    // Mapped base path from API (human-readable path to the current location)
    this.mappedBasePath = '';
    
    // File and folder lists
    this.items = []; // Combined folders and files
    this.folders = [];
    this.files = [];
    this.loading = false;
    
    // Sorting
    this.sortBy = 'name';
    this.sortReverse = false;
    
    // File upload
    this.uploading = false;
    this.uploadProgress = 0;
    this.dragOver = false; // Track drag over state
  }
  
  $onInit() {
    this.loadFiles();
    
    // Set up file input event listener
    const self = this;
    this.$scope.$evalAsync(() => {
      const fileInput = self.$element[0].querySelector('#s3-file-upload-input');
      if (fileInput) {
        fileInput.addEventListener('change', function(event) {
          self.onFileSelect(event);
        });
      }
      
      // Set up drag and drop handlers
      const dropZone = self.$element[0].querySelector('.s3-drop-zone');
      if (dropZone) {
        // Prevent default drag behaviors
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
          dropZone.addEventListener(eventName, function(e) {
            e.preventDefault();
            e.stopPropagation();
          }, false);
        });
        
        // Highlight drop zone when item is dragged over it
        dropZone.addEventListener('dragenter', function() {
          self.dragOver = true;
          self.$scope.$apply();
        });
        
        dropZone.addEventListener('dragover', function() {
          self.dragOver = true;
          self.$scope.$apply();
        });
        
        dropZone.addEventListener('dragleave', function(e) {
          // Only set dragOver to false if we're leaving the drop zone itself
          if (!dropZone.contains(e.relatedTarget)) {
            self.dragOver = false;
            self.$scope.$apply();
          }
        });
        
        dropZone.addEventListener('drop', function(e) {
          self.dragOver = false;
          self.$scope.$apply();
          
          const files = e.dataTransfer.files;
          if (files && files.length > 0) {
            self.handleDroppedFiles(files);
          }
        });
      }
    });
  }
  
  $onDestroy() {
    // Clean up event listeners
    const fileInput = this.$element[0].querySelector('#s3-file-upload-input');
    if (fileInput) {
      fileInput.removeEventListener('change', this.onFileSelect);
    }
  }
  
  /**
   * Handle files dropped onto the file browser
   */
  handleDroppedFiles(files) {
    const self = this;
    const fileArray = Array.from(files);
    
    // Filter out directories (they don't have a size or are empty)
    const filesToUpload = fileArray.filter(file => {
      // Check if it's a file (not a directory)
      return file.size > 0 || file.type !== '';
    });
    
    if (filesToUpload.length === 0) {
      this.toastr.warning('No valid files to upload', 'Warning');
      return;
    }
    
    // Upload each file
    filesToUpload.forEach((file, index) => {
      // Small delay between uploads to avoid overwhelming the server
      setTimeout(() => {
        this.uploadFile(file);
      }, index * 100);
    });
  }
  
  /**
   * Load files and folders for the current path
   */
  loadFiles() {
    if (!this.baseApiUrl) {
      console.error('baseApiUrl is required for s3-file-browser');
      return;
    }
    
    this.loading = true;
    const url = `${this.baseApiUrl}/list`;
    const fullUrl = url.startsWith('http') ? url : this.$rootScope.apiHost + url;
    // Use actualCurrentPath for API calls (with IDs)
    const params = this.actualCurrentPath ? { prefix: this.actualCurrentPath } : {};
    
    const baseUrl = this.baseApiUrl.startsWith('http') ? this.baseApiUrl : this.$rootScope.apiHost + this.baseApiUrl;
    
    this.$http.get(fullUrl, { 
      params: params,
      withCredentials: true 
    }).then((response) => {
      const data = response.data || {};
      
      // Store mappedBasePath from API response
      if (data.mappedBasePath !== undefined) {
        this.mappedBasePath = data.mappedBasePath;
      }
      
      // Map folders - use path (display) and actualPath (for API) from API
      const folders = (data.folders || []).map(folder => ({
        name: folder.name || folder,
        path: folder.path || (this.currentPath ? `${this.currentPath}/${folder.name || folder}` : (folder.name || folder)),
        actualPath: folder.actualPath || folder.path || (this.actualCurrentPath ? `${this.actualCurrentPath}/${folder.name || folder}` : (folder.name || folder)),
        lastModified: folder.lastModified,
        isFolder: true,
        size: null,
        sizeLabel: '-',
        contentType: 'folder',
        icon: 'fa-folder',
        iconColor: '#f39c12'
      }));
      
      // Map files
      const files = (data.files || []).map(file => ({
        name: file.name || file.key,
        key: file.key || file.name,
        size: file.size || file.fileSize || 0,
        sizeLabel: this.bytesToSize(file.size || file.fileSize || 0),
        contentType: file.contentType || file.type || 'application/octet-stream',
        lastModified: file.lastModified || file.dateAdded,
        url: file.url || `${baseUrl}/download?key=${encodeURIComponent(file.key || file.name)}`,
        isFolder: false,
        icon: this.getFileIconClass({ contentType: file.contentType || file.type || 'application/octet-stream', name: file.name || file.key }),
        iconColor: '#3498db'
      }));
      
      // Combine and sort: folders first, then files, both alphabetically
      this.items = [...folders, ...files].sort((a, b) => {
        // Folders first
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        // Then alphabetically by name
        return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
      });
      
      // Keep separate arrays for backward compatibility if needed
      this.folders = folders;
      this.files = files;
      
      this.loading = false;
    }).catch((error) => {
      console.error('Error loading files:', error);
      this.toastr.error('Failed to load files', 'Error');
      this.loading = false;
    });
  }
  
  /**
   * Update breadcrumbs based on current path
   * Uses restPath for API calls and displayPath for UI display
   */
  updateBreadcrumbs() {
    // Always start with Home
    this.breadcrumbs = [{ name: 'Home', restPath: '', displayPath: '' }];
    
    // Only add breadcrumbs if we have paths
    if (this.currentPath && this.actualCurrentPath) {
      

      // Remove mappedBasePath from currentPath to get relative display path
      let relativeDisplayPath = this.currentPath;

      if (this.mappedBasePath && this.currentPath.startsWith(this.mappedBasePath)) {
        relativeDisplayPath = this.currentPath.replace(this.mappedBasePath, '').replace(/^\//, '');
      }
      
      // Split both paths to build breadcrumbs
      const displaySegments = relativeDisplayPath.split('/').filter(s => s);
      let actualSegments = this.actualCurrentPath.split('/').filter(s => s);
      
      // Remove initial elements from actualSegments until it matches displaySegments length
      if (actualSegments.length > displaySegments.length) {
        const elementsToRemove = actualSegments.length - displaySegments.length;
        actualSegments = actualSegments.slice(elementsToRemove);
      }

   
      // Build breadcrumbs incrementally
      let accumulatedDisplayPath = '';
      let accumulatedActualPath = '';
      
      displaySegments.forEach((segment, index) => {
        // Build display path (human-readable)
        accumulatedDisplayPath = accumulatedDisplayPath ? `${accumulatedDisplayPath}/${segment}` : segment;
        
        // Build actual path incrementally using the corresponding actual segment
        if (index < actualSegments.length) {
          accumulatedActualPath = accumulatedActualPath ? `${accumulatedActualPath}/${actualSegments[index]}` : actualSegments[index];
        } else {
          // Fallback if actual path is shorter
          accumulatedActualPath = accumulatedActualPath ? `${accumulatedActualPath}/${segment}` : segment;
        }


        let base  = "Synergist_Computing_LLC/customers/14/contracts/26";

        this.breadcrumbs.push({ 
          name: segment, 
          restPath: base+'/'+accumulatedActualPath +'/', // Use accumulated actual path for REST API calls
          displayPath: accumulatedDisplayPath // Use display path for UI
        });
      });
    }

    console.log('this.breadcrumbs', this.breadcrumbs);
  }
  
  /**
   * Navigate into a folder
   */
  navigateToFolder(folderPath) {
    // folderPath can be an object with path and actualPath (from folder click), 
    // or an object with restPath and displayPath (from breadcrumb click)
    let displayPath, actualPath;
    
    
    if (typeof folderPath === 'object') {
      if (folderPath.path && folderPath.actualPath) {
        // From folder click - use the folder object
        displayPath = folderPath.path;
        actualPath = folderPath.actualPath;
      } else if (folderPath.restPath !== undefined) {
        // From breadcrumb click - use restPath and displayPath
        actualPath = folderPath.restPath;
        displayPath = folderPath.displayPath || actualPath;
      } else {
        // Fallback
        displayPath = folderPath.path || folderPath;
        actualPath = folderPath.actualPath || folderPath.path || folderPath;
      }
    } else {
      // String fallback - find the folder
      const folder = this.items.find(item => 
        item.isFolder && 
        (item.path === folderPath || item.actualPath === folderPath)
      );
      displayPath = folder ? folder.path : folderPath;
      actualPath = folder ? (folder.actualPath || folder.path) : folderPath;
    }
    
    this.pathHistory.push(this.currentPath);
    this.actualPathHistory.push(this.actualCurrentPath);
    this.currentPath = displayPath;
    this.actualCurrentPath = actualPath;

    this.updateBreadcrumbs();
    this.loadFiles();
  }
  
  /**
   * Navigate to parent folder
   */
  navigateUp() {
    if (this.pathHistory.length > 0 && this.actualPathHistory.length > 0) {
      this.currentPath = this.pathHistory.pop();
      this.actualCurrentPath = this.actualPathHistory.pop();
      this.updateBreadcrumbs();
      this.loadFiles();
    } else if (this.currentPath && this.actualCurrentPath) {
      // Go to root
      const displayParts = this.currentPath.split('/');
      displayParts.pop();
      this.currentPath = displayParts.join('/');
      
      const actualParts = this.actualCurrentPath.split('/');
      actualParts.pop();
      this.actualCurrentPath = actualParts.join('/');
      
      this.pathHistory = [];
      this.actualPathHistory = [];
      this.updateBreadcrumbs();
      this.loadFiles();
    }
  }
  
  /**
   * Navigate to root
   */
  navigateToRoot() {
    this.currentPath = '';
    this.actualCurrentPath = '';
    this.pathHistory = [];
    this.actualPathHistory = [];
    this.updateBreadcrumbs();
    this.loadFiles();
  }
  
  /**
   * Handle file selection for preview - opens modal
   */
  selectFile(file) {
    if (file.isFolder) {
      return; // Don't open preview for folders
    }
    
    const self = this;
    const modalTemplate = `
      <div class="inmodal">
        <div class="color-line"></div>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fa fa-eye"></i> Preview: {{file.name}}
          </h4>
        </div>
        <div class="modal-body" style="max-height: 70vh; overflow-y: auto;">
          <!-- Image Preview -->
          <div ng-if="file.contentType && file.contentType.startsWith('image/')">
            <img ng-src="{{previewUrl}}" 
                 class="img-responsive" 
                 style="max-width: 100%; margin-bottom: 15px;"
                 alt="{{file.name}}">
          </div>
          
          <!-- PDF Preview -->
          <div ng-if="file.contentType === 'application/pdf'">
            <iframe ng-src="{{previewUrl}}" 
                    style="width: 100%; height: 600px; border: none;">
            </iframe>
          </div>
          
          <!-- Text Preview -->
          <div ng-if="file.contentType && file.contentType.startsWith('text/')">
            <pre style="max-height: 500px; overflow-y: auto; background-color: #f5f5f5; padding: 15px; border-radius: 4px;">Loading...</pre>
            <p class="text-muted">Text preview not available. Click download to view the file.</p>
          </div>
          
          <!-- Default Preview -->
          <div ng-if="!file.contentType || (!file.contentType.startsWith('image/') && file.contentType !== 'application/pdf' && !file.contentType.startsWith('text/'))">
            <div style="text-align: center; padding: 40px;">
              <i class="fa" ng-class="getFileIconClass(file)" style="font-size: 64px; color: #ccc; margin-bottom: 15px;"></i>
              <p class="text-muted">Preview not available for this file type.</p>
              <button class="btn btn-primary" ng-click="downloadFile()">
                <i class="fa fa-download"></i> Download File
              </button>
            </div>
          </div>
          
          <!-- File Details -->
          <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #ddd;">
            <h5>File Details</h5>
            <table class="table table-condensed">
              <tr>
                <td><strong>Name:</strong></td>
                <td>{{file.name}}</td>
              </tr>
              <tr>
                <td><strong>Size:</strong></td>
                <td>{{file.sizeLabel}}</td>
              </tr>
              <tr>
                <td><strong>Type:</strong></td>
                <td>{{file.contentType}}</td>
              </tr>
              <tr>
                <td><strong>Last Modified:</strong></td>
                <td>{{file.lastModified | date:'medium'}}</td>
              </tr>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" ng-click="downloadFile()">
            <i class="fa fa-download"></i> Download
          </button>
          <button class="btn btn-danger" ng-click="deleteFile()">
            <i class="fa fa-trash"></i> Delete
          </button>
          <button type="button" class="btn btn-default" ng-click="close()">Close</button>
        </div>
      </div>
    `;
    
    const modalInstance = this.$uibModal.open({
      template: modalTemplate,
      controller: ['$scope', '$uibModalInstance', 'file', 'getFileIconClass', function($scope, $uibModalInstance, file, getFileIconClass) {
        $scope.file = file;
        $scope.previewUrl = file.url;
        $scope.getFileIconClass = getFileIconClass;
        
        $scope.downloadFile = function() {
          window.open(file.url, '_blank');
        };
        
        $scope.deleteFile = function() {
          $uibModalInstance.close('delete');
        };
        
        $scope.close = function() {
          $uibModalInstance.dismiss('cancel');
        };
      }],
      size: 'lg',
      windowClass: 'hmodal-info animated zoomIn',
      resolve: {
        file: () => file,
        getFileIconClass: () => (file) => self.getFileIconClass(file)
      }
    });
    
    modalInstance.result.then((result) => {
      if (result === 'delete') {
        // File was deleted from modal, refresh the file list
        this.deleteFile(file);
      }
    }).catch(() => {
      // Modal dismissed
    });
  }
  
  /**
   * Download a file
   */
  downloadFile(file) {
    window.open(file.url, '_blank');
  }
  
  /**
   * Delete a file
   */
  deleteFile(file) {
    const self = this;
    const modalTemplate = `
      <div class="inmodal">
        <div class="color-line" style="background-color: #d9534f;"></div>
        <div class="modal-header" style="background-color: #d9534f; color: #fff;">
          <h5 class="modal-title">
            <i class="fa fa-exclamation-triangle"></i> Delete File
          </h5>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>{{fileName}}</strong>?</p>
          <p class="text-muted">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-default" ng-click="cancel()">Cancel</button>
          <button type="button" class="btn btn-danger" ng-click="confirmDelete()">
            <i class="fa fa-trash"></i> Delete File
          </button>
        </div>
      </div>
    `;
    
    const modalInstance = this.$uibModal.open({
      template: modalTemplate,
      controller: ['$scope', '$uibModalInstance', function($scope, $uibModalInstance) {
        $scope.fileName = file.name;
        
        $scope.confirmDelete = function() {
          $uibModalInstance.close('delete');
        };
        
        $scope.cancel = function() {
          $uibModalInstance.dismiss('cancel');
        };
      }],
      size: 'md',
      windowClass: 'hmodal-danger animated zoomIn'
    });
    
    modalInstance.result.then((result) => {
      if (result === 'delete') {
        const url = `${this.baseApiUrl}/delete`;
        const fullUrl = url.startsWith('http') ? url : this.$rootScope.apiHost + url;
        this.$http.delete(fullUrl, {
          params: { key: file.key },
          withCredentials: true
        }).then(() => {
          this.toastr.success('File deleted successfully', 'Success');
          this.loadFiles();
        }).catch((error) => {
          console.error('Error deleting file:', error);
          this.toastr.error('Failed to delete file', 'Error');
        });
      }
    }).catch(() => {
      // Modal dismissed
    });
  }
  
  /**
   * Handle file input change for upload
   */
  onFileSelect(event) {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }
    
    Array.from(files).forEach(file => {
      this.uploadFile(file);
    });
    
    // Reset input
    event.target.value = '';
  }
  
  /**
   * Upload a file to the current folder
   */
  uploadFile(file) {
    // Validate file type if restrictions are set
    if (this.allowedFileTypes) {
      const allowedTypes = this.allowedFileTypes.split(',').map(t => t.trim());
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.some(type => {
        return file.type.match(type.replace('*', '.*')) || 
               fileExtension === type || 
               file.name.toLowerCase().endsWith(type.toLowerCase());
      });
      
      if (!isValidType) {
        this.toastr.error(`File type not allowed. Allowed types: ${this.allowedFileTypes}`, 'Error');
        return;
      }
    }
    
    // Validate file size if restriction is set
    if (this.maxFileSize && file.size > parseInt(this.maxFileSize)) {
      this.toastr.error(`File size exceeds maximum allowed size of ${this.bytesToSize(this.maxFileSize)}`, 'Error');
      return;
    }
    
    this.uploading = true;
    this.uploadProgress = 0;
    
    const formData = new FormData();
    formData.append('file', file);
    
    const url = `${this.baseApiUrl}/upload`;
    // Use actualCurrentPath for API calls
    const params = this.actualCurrentPath ? { prefix: this.actualCurrentPath } : {};
    
    const xhr = new XMLHttpRequest();
    
    // Track upload progress
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        this.uploadProgress = Math.round((e.loaded / e.total) * 100);
        this.$scope.$apply();
      }
    });
    
    // Build URL with query params
    let fullUrl = url.startsWith('http') ? url : this.$rootScope.apiHost + url;
    const queryParams = [];
    if (this.actualCurrentPath) {
      queryParams.push(`prefix=${encodeURIComponent(this.actualCurrentPath)}`);
    }
    if (queryParams.length > 0) {
      fullUrl += (fullUrl.includes('?') ? '&' : '?') + queryParams.join('&');
    }
    
    xhr.open('POST', fullUrl);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.withCredentials = true;
    
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        this.toastr.success(`File "${file.name}" uploaded successfully`, 'Success');
        this.uploading = false;
        this.uploadProgress = 0;
        this.loadFiles();
      } else {
        this.toastr.error('Failed to upload file', 'Error');
        this.uploading = false;
        this.uploadProgress = 0;
      }
    };
    
    xhr.onerror = () => {
      this.toastr.error('Failed to upload file', 'Error');
      this.uploading = false;
      this.uploadProgress = 0;
    };
    
    xhr.send(formData);
  }
  
  /**
   * Create a new folder
   */
  createFolder() {
    const self = this;
    const modalTemplate = `
      <div class="inmodal">
        <div class="color-line"></div>
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fa fa-folder-plus"></i> Create New Folder
          </h5>
        </div>
        <div class="modal-body">
          <form name="folderForm" novalidate>
            <div class="form-group">
              <label for="folderName">Folder Name <span class="text-danger">*</span></label>
              <input 
                type="text" 
                id="folderName"
                class="form-control" 
                ng-model="folderName"
                ng-keyup="$event.keyCode === 13 && !folderForm.$invalid && createFolder()"
                placeholder="Enter folder name"
                required
                autofocus>
              <small class="help-block text-muted">Only letters, numbers, spaces, hyphens, and underscores are allowed.</small>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-default" ng-click="cancel()">Cancel</button>
          <button type="button" class="btn btn-success" ng-click="createFolder()" ng-disabled="!folderName || !folderName.trim()">
            <i class="fa fa-folder-plus"></i> Create Folder
          </button>
        </div>
      </div>
    `;
    
    const modalInstance = this.$uibModal.open({
      template: modalTemplate,
      controller: ['$scope', '$uibModalInstance', function($scope, $uibModalInstance) {
        $scope.folderName = '';
        
        $scope.createFolder = function() {
          if (!$scope.folderName || !$scope.folderName.trim()) {
            return;
          }
          
          const sanitizedName = $scope.folderName.trim().replace(/[^a-zA-Z0-9-_ ]/g, '');
          if (!sanitizedName) {
            self.toastr.error('Invalid folder name. Only letters, numbers, spaces, hyphens, and underscores are allowed.', 'Error');
            return;
          }
          
          $uibModalInstance.close(sanitizedName);
        };
        
        $scope.cancel = function() {
          $uibModalInstance.dismiss('cancel');
        };
      }],
      size: 'md',
      windowClass: 'hmodal-info animated zoomIn'
    });
    
    modalInstance.result.then((folderName) => {
      const url = `${this.baseApiUrl}/create-folder`;
      const fullUrl = url.startsWith('http') ? url : this.$rootScope.apiHost + url;
      const params = {
        folderName: folderName
      };
      // Use actualCurrentPath for API calls
      if (this.actualCurrentPath) {
        params.prefix = this.actualCurrentPath;
      }
      
      this.$http.post(fullUrl, null, {
        params: params,
        withCredentials: true
      }).then(() => {
        this.toastr.success(`Folder "${folderName}" created successfully`, 'Success');
        this.loadFiles();
      }).catch((error) => {
        console.error('Error creating folder:', error);
        this.toastr.error('Failed to create folder', 'Error');
      });
    }).catch(() => {
      // Modal dismissed
    });
  }
  
  /**
   * Delete a folder
   */
  deleteFolder(folder) {
    const self = this;
    const modalTemplate = `
      <div class="inmodal">
        <div class="color-line" style="background-color: #d9534f;"></div>
        <div class="modal-header" style="background-color: #d9534f; color: #fff;">
          <h5 class="modal-title">
            <i class="fa fa-exclamation-triangle"></i> Delete Folder
          </h5>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete the folder <strong>{{folderName}}</strong> and all its contents?</p>
          <p class="text-danger"><strong>Warning:</strong> This will permanently delete all files and subfolders within this folder. This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-default" ng-click="cancel()">Cancel</button>
          <button type="button" class="btn btn-danger" ng-click="confirmDelete()">
            <i class="fa fa-trash"></i> Delete Folder
          </button>
        </div>
      </div>
    `;
    
    const modalInstance = this.$uibModal.open({
      template: modalTemplate,
      controller: ['$scope', '$uibModalInstance', function($scope, $uibModalInstance) {
        $scope.folderName = folder.name;
        
        $scope.confirmDelete = function() {
          $uibModalInstance.close('delete');
        };
        
        $scope.cancel = function() {
          $uibModalInstance.dismiss('cancel');
        };
      }],
      size: 'md',
      windowClass: 'hmodal-danger animated zoomIn'
    });
    
    modalInstance.result.then((result) => {
      if (result === 'delete') {
        const url = `${this.baseApiUrl}/delete-folder`;
        const fullUrl = url.startsWith('http') ? url : this.$rootScope.apiHost + url;
        this.$http.delete(fullUrl, {
          params: { prefix: folder.path },
          withCredentials: true
        }).then(() => {
          this.toastr.success(`Folder "${folder.name}" deleted successfully`, 'Success');
          this.loadFiles();
        }).catch((error) => {
          console.error('Error deleting folder:', error);
          this.toastr.error('Failed to delete folder', 'Error');
        });
      }
    }).catch(() => {
      // Modal dismissed
    });
  }
  
  /**
   * Format bytes to human readable size
   */
  bytesToSize(bytes, decimals = 2) {
    if (!Number(bytes)) {
      return '0 B';
    }
    
    const kbToBytes = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    
    const index = Math.floor(Math.log(bytes) / Math.log(kbToBytes));
    
    return `${parseFloat(
      (bytes / Math.pow(kbToBytes, index)).toFixed(dm)
    )} ${sizes[index]}`;
  }
  
  /**
   * Get file icon class based on file type
   */
  getFileIcon(file) {
    return this.getFileIconClass(file);
  }
  
  /**
   * Get file icon class based on file type (returns class name)
   */
  getFileIconClass(file) {
    const contentType = (file.contentType || '').toLowerCase();
    const extension = (file.name || '').split('.').pop().toLowerCase();
    
    if (contentType.includes('pdf') || extension === 'pdf') {
      return 'fa-file-pdf';
    } else if (contentType.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
      return 'fa-file-image';
    } else if (contentType.includes('word') || ['doc', 'docx'].includes(extension)) {
      return 'fa-file-word';
    } else if (contentType.includes('excel') || contentType.includes('spreadsheet') || ['xls', 'xlsx'].includes(extension)) {
      return 'fa-file-excel';
    } else if (contentType.includes('powerpoint') || ['ppt', 'pptx'].includes(extension)) {
      return 'fa-file-powerpoint';
    } else if (contentType.includes('text') || ['txt', 'csv'].includes(extension)) {
      return 'fa-file-alt';
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return 'fa-file-archive';
    } else {
      return 'fa-file';
    }
  }
  
  /**
   * Handle item click (folder or file)
   */
  onItemClick(item) {
    if (item.isFolder) {
      this.navigateToFolder(item);
    } else {
      this.selectFile(item);
    }
  }
  
  /**
   * Handle item double click
   */
  onItemDoubleClick(item) {
    if (item.isFolder) {
      this.navigateToFolder(item);
    } else {
      this.downloadFile(item);
    }
  }
  
  /**
   * Get sorted items based on current sort settings
   */
  getSortedItems() {
    if (!this.items || this.items.length === 0) {
      return [];
    }
    
    const sorted = [...this.items].sort((a, b) => {
      // Always keep folders first when sorting by name
      if (this.sortBy === 'name') {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
      }
      
      let aVal, bVal;
      
      switch (this.sortBy) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'size':
          aVal = a.size || 0;
          bVal = b.size || 0;
          // Folders have no size, put them first
          if (a.isFolder && !b.isFolder) return -1;
          if (!a.isFolder && b.isFolder) return 1;
          break;
        case 'contentType':
          aVal = (a.contentType || '').toLowerCase();
          bVal = (b.contentType || '').toLowerCase();
          break;
        case 'lastModified':
          aVal = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          bVal = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          break;
        default:
          aVal = a.name || '';
          bVal = b.name || '';
      }
      
      if (aVal < bVal) {
        return this.sortReverse ? 1 : -1;
      }
      if (aVal > bVal) {
        return this.sortReverse ? -1 : 1;
      }
      return 0;
    });
    
    return sorted;
  }
}

export default S3FileBrowserController;




// WEBPACK FOOTER //
// ./client/common/directives/s3-file-browser/s3-file-browser.controller.js